import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  UserCog,
  FileText,
  ClipboardList,
  AlertTriangle,
  Pill,
  GraduationCap,
  Shield,
  PoundSterling,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Building2,
  Clock,
  FolderOpen,
  Home,
  Heart,
  UserCheck,
  ClipboardCheck,
  Bot,
  Bell,
  CalendarDays
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const menuItems = [
  { 
    id: 'dashboard', 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    path: '/dashboard',
    roles: ['admin', 'manager', 'care_worker', 'hr', 'finance']
  },
  { 
    id: 'clients', 
    label: 'Clients & Residents', 
    icon: Heart, 
    path: '/clients',
    roles: ['admin', 'manager', 'care_worker']
  },
  { 
    id: 'staff', 
    label: 'Staff Management', 
    icon: UserCog, 
    path: '/staff',
    roles: ['admin', 'manager', 'hr']
  },
  { 
    id: 'hr-suite', 
    label: 'HR Suite', 
    icon: UserCheck, 
    path: '/hr-suite',
    roles: ['admin', 'manager', 'hr']
  },
  { 
    id: 'clock', 
    label: 'Clock In/Out', 
    icon: Clock, 
    path: '/clock',
    roles: ['admin', 'manager', 'care_worker', 'hr']
  },
  { 
    id: 'scheduling', 
    label: 'Scheduling', 
    icon: CalendarDays, 
    path: '/scheduling',
    roles: ['admin', 'manager']
  },
  { 
    id: 'care-plans', 
    label: 'Care Plans', 
    icon: ClipboardList, 
    path: '/care-plans',
    roles: ['admin', 'manager', 'care_worker']
  },
  { 
    id: 'daily-logs', 
    label: 'Daily Logs', 
    icon: FileText, 
    path: '/daily-logs',
    roles: ['admin', 'manager', 'care_worker']
  },
  { 
    id: 'incidents', 
    label: 'Incidents', 
    icon: AlertTriangle, 
    path: '/incidents',
    roles: ['admin', 'manager', 'care_worker']
  },
  { 
    id: 'medications', 
    label: 'MAR Charts', 
    icon: Pill, 
    path: '/medications',
    roles: ['admin', 'manager', 'care_worker']
  },
  { 
    id: 'documents', 
    label: 'Documents', 
    icon: FolderOpen, 
    path: '/documents',
    roles: ['admin', 'manager', 'care_worker', 'hr']
  },
  { 
    id: 'training', 
    label: 'Training', 
    icon: GraduationCap, 
    path: '/training',
    roles: ['admin', 'manager', 'care_worker', 'hr']
  },
  { 
    id: 'compliance', 
    label: 'CQC Compliance', 
    icon: Shield, 
    path: '/compliance',
    roles: ['admin', 'manager']
  },
  { 
    id: 'quality-assurance', 
    label: 'Quality Assurance', 
    icon: ClipboardCheck, 
    path: '/quality-assurance',
    roles: ['admin', 'manager']
  },
  { 
    id: 'ai-assistant', 
    label: 'AI Assistant', 
    icon: Bot, 
    path: '/ai-assistant',
    roles: ['admin', 'manager']
  },
  { 
    id: 'cqc-readiness', 
    label: 'CQC Readiness', 
    icon: Shield, 
    path: '/cqc-readiness',
    roles: ['admin', 'manager']
  },
  { 
    id: 'notification-settings', 
    label: 'Notifications', 
    icon: Bell, 
    path: '/notification-settings',
    roles: ['admin', 'manager', 'care_worker']
  },
  { 
    id: 'finance', 
    label: 'Finance & Payroll', 
    icon: PoundSterling, 
    path: '/finance',
    roles: ['admin', 'finance']
  },
  { 
    id: 'settings', 
    label: 'Settings', 
    icon: Settings, 
    path: '/settings',
    roles: ['admin']
  },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState({});
  const location = useLocation();
  const { user, logout, organization } = useAuth();

  // Fetch notification counts
  useEffect(() => {
    if (organization?.id) {
      fetchNotifications();
      // Refresh every 2 minutes
      const interval = setInterval(fetchNotifications, 120000);
      return () => clearInterval(interval);
    }
  }, [organization]);

  const fetchNotifications = async () => {
    try {
      // Fetch multiple counts in parallel
      const [readinessRes, incidentsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/readiness/daily-check`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/incidents`, { params: { organization_id: organization?.id, status: 'open' } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/dashboard/stats`, { params: { organization_id: organization?.id } }).catch(() => ({ data: {} }))
      ]);

      const counts = {
        'cqc-readiness': readinessRes.data?.urgent_actions?.length || 0,
        'incidents': Array.isArray(incidentsRes.data) ? incidentsRes.data.length : 0,
        'documents': statsRes.data?.pending_documents || 0,
        'hr-suite': (statsRes.data?.supervisions_due || 0) + (statsRes.data?.appraisals_due || 0),
        'training': statsRes.data?.training_compliance < 100 ? Math.round(100 - (statsRes.data?.training_compliance || 0)) : 0
      };

      setNotifications(counts);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const getNotificationBadge = (itemId) => {
    const count = notifications[itemId];
    if (!count || count === 0) return null;

    // Different colors based on item type
    const badgeColors = {
      'cqc-readiness': 'bg-amber-500',
      'incidents': 'bg-red-500',
      'documents': 'bg-blue-500',
      'hr-suite': 'bg-purple-500',
      'training': 'bg-orange-500'
    };

    return (
      <span className={cn(
        "absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1",
        badgeColors[itemId] || 'bg-teal-500'
      )}>
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <TooltipProvider>
      <aside 
        className={cn(
          "fixed left-0 top-0 h-screen bg-slate-900 text-white z-50 flex flex-col transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
              <Home className="w-5 h-5 text-white" />
            </div>
            {!collapsed && (
              <div className="animate-fade-in">
                <h1 className="font-bold text-lg leading-tight">D3 Care</h1>
                <p className="text-xs text-slate-400">Solutions</p>
              </div>
            )}
          </Link>
        </div>

        {/* Organization */}
        {organization && !collapsed && (
          <div className="px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span className="text-slate-300 truncate">{organization.name}</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {filteredMenuItems.map((item) => {
              const isActive = location.pathname === item.path || 
                              location.pathname.startsWith(item.path + '/');
              const Icon = item.icon;
              const badge = getNotificationBadge(item.id);

              const linkContent = (
                <Link
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 relative",
                    isActive 
                      ? "bg-teal-600 text-white" 
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                  data-testid={`nav-${item.id}`}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {collapsed && badge}
                  </div>
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium flex-1">{item.label}</span>
                      {badge && (
                        <span className={cn(
                          "min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1",
                          item.id === 'incidents' ? 'bg-red-500' :
                          item.id === 'cqc-readiness' ? 'bg-amber-500' :
                          item.id === 'documents' ? 'bg-blue-500' :
                          item.id === 'hr-suite' ? 'bg-purple-500' :
                          item.id === 'training' ? 'bg-orange-500' : 'bg-teal-500'
                        )}>
                          {notifications[item.id] > 99 ? '99+' : notifications[item.id]}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );

              return (
                <li key={item.id}>
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {linkContent}
                      </TooltipTrigger>
                      <TooltipContent side="right" className="flex items-center gap-2">
                        {item.label}
                        {notifications[item.id] > 0 && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                            {notifications[item.id]}
                          </Badge>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    linkContent
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User section */}
        <div className="border-t border-slate-800 p-4">
          {!collapsed && user && (
            <div className="mb-3">
              <p className="text-sm font-medium text-white truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-400 capitalize">{user.role?.replace('_', ' ')}</p>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="flex-1 justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {!collapsed && <span className="ml-2">Logout</span>}
            </Button>
          </div>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-slate-300 hover:bg-slate-600 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
